import { Component, ElementRef, forwardRef, inject, input, OnInit, viewChild, ViewChild } from '@angular/core';
import { BehaviorSubject, catchError, combineLatest, debounceTime, distinctUntilChanged, exhaustMap, of, startWith, Subject, switchMap, tap, } from 'rxjs';
import { EquipmentItemDto, EquipmentService, SortDto } from '@artemis-software/wr-api';
import { AbstractFormControl } from '@shared/form-controls/abstract-form-control';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Router } from '@angular/router';
import { NbPopoverDirective } from '@nebular/theme';
import { toObservable } from '@angular/core/rxjs-interop';
import OrderEnum = SortDto.OrderEnum;

@Component({
  standalone: false,
  selector: 'wr-tga-type-select',
  templateUrl: './tga-type-select.component.html',
  styleUrls: ['./tga-type-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TgaTypeSelectComponent),
      multi: true,
    },
  ],
})
export class TgaTypeSelectComponent extends AbstractFormControl<EquipmentItemDto | undefined> implements OnInit {

  readonly equipmentService = inject(EquipmentService);
  readonly router = inject(Router);

  popover = viewChild(NbPopoverDirective);

  @ViewChild('searchInput')
  input?: ElementRef;

  tenantId = input<string | undefined>();
  tenantId$ = toObservable(this.tenantId);

  filterText$ = new BehaviorSubject<string>('');

  nextPage = 0;

  private readonly pageSize = 10;

  private hasMore = true;

  private lastResetFailed = false;

  private resetToken = 0;

  loadMore$ = new Subject<void>();

  equipments$ = new BehaviorSubject<EquipmentItemDto[]>([]);

  ngOnInit(): void {
    combineLatest([
      this.filterText$.pipe(
        debounceTime(300),
        distinctUntilChanged((previous, current) => previous === current && !this.lastResetFailed),
      ),
      this.tenantId$.pipe(startWith(this.tenantId())),
    ])
      .pipe(
        switchMap(([filterText, tenantId]) => {
          const token = ++this.resetToken;
          this.nextPage = 0;
          this.hasMore = true;
          this.lastResetFailed = false;
          return this.fetchEquipments(filterText, tenantId, 0).pipe(
            tap(equipments => {
              if (token !== this.resetToken) return;
              this.equipments$.next(equipments);
              this.nextPage = 1;
              this.hasMore = equipments.length === this.pageSize;
            }),
            catchError(() => {
              if (token === this.resetToken) {
                this.equipments$.next([]);
                this.lastResetFailed = true;
              }
              return of([] as EquipmentItemDto[]);
            }),
          );
        }),
      )
      .subscribe();

    this.loadMore$
      .pipe(
        exhaustMap(() => {
          if (this.nextPage === 0 || !this.hasMore) return of([] as EquipmentItemDto[]);
          const token = this.resetToken;
          const page = this.nextPage;
          return this.fetchEquipments(this.filterText$.value, this.tenantId(), page).pipe(
            tap(equipments => {
              if (token !== this.resetToken) return;
              const current = this.equipments$.value || [];
              this.equipments$.next([...current, ...equipments]);
              this.nextPage = page + 1;
              this.hasMore = equipments.length === this.pageSize;
            }),
            catchError(() => of([] as EquipmentItemDto[])),
          );
        }),
      )
      .subscribe();
  }

  private fetchEquipments(filterText: string, tenantId: string | undefined, page: number) {
    const appliedFilterText = filterText && filterText.length > 0 ? filterText : undefined;
    return this.equipmentService.findAll({
      text: appliedFilterText,
      tenantId: tenantId,
      page: page,
      deprecated: false,
      size: this.pageSize,
      sort: {
        sort: 'name',
        order: OrderEnum.Asc,
      },
    });
  }

  getEquipmentTitle(equipment: EquipmentItemDto | undefined): string {
    if (!equipment) {
      return '';
    }
    return equipment.name + ' (' + equipment.tenant.name + ')';
  }

  override writeValue(equipment: EquipmentItemDto): void {
    super.writeValue(equipment);
    this.filterText$.next(equipment?.name ?? '');
  }

  onSelectionChange(equipment?: EquipmentItemDto): void {
    if (equipment?.id) {
      this.filterText$.next(equipment.name);
      this.value = equipment;
      this.popover()?.hide();
    }
  }

  clear(): void {
    this.value = undefined;
    this.filterText$.next('');
  }

  goToEquipment(): void {
    this.router.navigate(['equipment', this.value?.id]);
  }
}
